/**
 * Press Pass — client-side access-code plumbing for The Prop Desk (specs/
 * props-dashboard D7 / §8a). The pass is a bearer secret: we hold it in
 * localStorage and send it as the `X-Press-Pass` header on /api/props reads.
 *
 * P3 scope is intentionally minimal (the design's P4 owns the mint UI, the
 * `/pass/verify` round-trip, and free-tier payload shaping): here we only
 * (a) read a `#pass=CODE` share fragment into storage and strip it from the
 * URL — fragment-only so the code never hits server logs or the Referer header
 * — and (b) expose get/set/clear. Whether the code is valid is decided by the
 * board endpoint returning 200 vs 401, not by anything here.
 */

const STORAGE_KEY = '2g1p_press_pass';
const INFO_KEY = '2g1p_press_pass_info';

/** Public-safe pass identity (NOT the code) — drives the "active" indicator. */
export interface StoredPassInfo {
  role: 'owner' | 'tester' | 'member';
  label: string;
}

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Private-mode / disabled storage — degrade to no-pass rather than throw.
    return null;
  }
}

/** The stored pass code, or null. */
export function getPass(): string | null {
  const code = safeLocalStorage()?.getItem(STORAGE_KEY)?.trim();
  return code ? code : null;
}

/** Persist a pass code (trimmed). Empty/whitespace clears it (and its info). */
export function setPass(code: string): void {
  const trimmed = code.trim();
  const store = safeLocalStorage();
  if (!store) return;
  if (trimmed) {
    store.setItem(STORAGE_KEY, trimmed);
  } else {
    store.removeItem(STORAGE_KEY);
    store.removeItem(INFO_KEY);
  }
}

/**
 * The verified pass identity ({role, label}) from the last successful
 * /pass/verify, or null. Non-secret — safe to render. Never contains the code.
 */
export function getPassInfo(): StoredPassInfo | null {
  const raw = safeLocalStorage()?.getItem(INFO_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredPassInfo;
    return parsed && typeof parsed.label === 'string' && typeof parsed.role === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** Cache the verified pass identity for the "Press Pass active: {label}" chip. */
export function setPassInfo(info: StoredPassInfo | null): void {
  const store = safeLocalStorage();
  if (!store) return;
  if (info) store.setItem(INFO_KEY, JSON.stringify({ role: info.role, label: info.label }));
  else store.removeItem(INFO_KEY);
}

/** Forget the stored pass + its identity (used when the API rejects it, 401). */
export function clearPass(): void {
  const store = safeLocalStorage();
  store?.removeItem(STORAGE_KEY);
  store?.removeItem(INFO_KEY);
}

/**
 * If the current URL carries a `#pass=CODE` fragment, store the code and strip
 * the fragment from the address bar (history.replaceState — no navigation, no
 * new history entry). Returns true when a fragment pass was consumed. Safe to
 * call on every mount.
 */
export function consumePassFragment(): boolean {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash; // e.g. "#pass=pp_abc123"
  const params = new URLSearchParams(hash.slice(1));
  if (!params.has('pass')) return false;

  // URLSearchParams handles malformed percent escapes without throwing, so a
  // broken invite fragment cannot strand the page in its loading state.
  const code = params.get('pass')?.trim() ?? '';
  if (code) setPass(code);

  // Remove only the pass token; keep any other fragment the app may use.
  params.delete('pass');
  const cleaned = params.toString();
  const url = window.location.pathname + window.location.search + (cleaned ? `#${cleaned}` : '');
  window.history.replaceState(null, '', url);
  return Boolean(code);
}
